<template>
  <div class="mx-auto" >
    <b-card border-variant="secondary"
            text-variant="secondary"
            :title="getTitle"
            align="center"
            style="max-width: 40rem;"
            class="mx-auto">
      <div v-if="this.deviceItem !== undefined">
        <DeviceDetailRowItem change-type="none" :data="this.deviceItem.hostname" :title="$t('device.hostname')"/>
        <DeviceDetailRowItem 
          v-if="isUpdateClientAliasAllowed"
          change-type="alias" 
          :data="this.deviceItem.alias" 
          :title="$t('device.alias')" 
          :isEditing="isEditingAlias"
          :editedValue="editedAlias"
          @changeDeviceItem="openChangeDeviceItemPage"
          @startEditing="startEditingAlias"
          @cancelEditing="cancelEditingAlias"
          @valueChanged="onAliasChanged"
          @saveEditing="saveAliasEditing"
        />
        <DeviceDetailRowItem change-type="none" :data="this.deviceItem.osType" :title="$t('device.osType')"/>
        <DeviceDetailRowItem change-type="none" :data="this.deviceItem.macAddress" :title="$t('device.macAddress')"/>
        <DeviceDetailRowItem change-type="lastSeenTime" :data="this.deviceItem.lastUpdatedTime" :title="$t('device.lastUpdatedTime')"/>
        <div class="mt-3 d-flex align-items-center justify-content-center">
          <b-button v-if="isUpdateClientAliasAllowed" @click="saveDevice" variant="outline-secondary" :disabled="!hasChanges" class="mb-0 me-4">{{ $t("buttonDefaults.save")}}</b-button>
          <b-button @click="deleteDevice" variant="outline-secondary" class="mb-0">{{ $t("device.removeDevice")}}</b-button>
        </div>
      </div>
    </b-card>
</div>      
</template>

<script>
import store from "@/store/store";
import router from "@/router"
import devicesApi from "@/services/devicesApi";
import identityDevicesApi from "@/services/identityDevicesApi";
import userApiService from "@/services/userApiService";
import DeviceDetailRowItem from "@/components/DeviceDetailRowItem.vue";

export default {
  name: "DeviceDetails",
  props: {
    deviceItem: { type: String },
    updated: { type: Boolean, default: false },
    errorUpdating: { type: Boolean, default: false }
  },
  components:{
    DeviceDetailRowItem
  },
  data() {
    return {
      isEditingAlias: false,
      editedAlias: '',
      originalAlias: ''
    }
  },
  computed: {
    isUpdateClientAliasAllowed() {
      return store.getters.isUpdateClientAliasAllowed
    },
    hasChanges() {
      // Don't allow null/empty alias - require non-empty trimmed value
      return this.isEditingAlias && this.editedAlias !== this.originalAlias && this.editedAlias.trim() !== ''
    },
    getTitle() {
      if(this.deviceItem === undefined){
        return "Unknown Device"
      } else if (this.deviceItem.type === "UNIT") {
        return this.$t('device.unitDeviceHeader')
      } else if (this.deviceItem.type === 'GUEST') {
        return this.$t('device.guestDeviceHeader')
      } else {
        return this.$t('device.identityDeviceHeader')
      }
    }
  },
  mounted()  {
    if(this.updated) {
      this.$bvToast.toast( this.$t('device.successfulDeviceUpdate'), {
            title: this.$t('device.deviceChangeTitle'),
            autoHideDelay: 10000,
            toaster: "b-toaster-top-center",
            variant: "success",
            append: false,
            solid: false
          })
    } else if(this.errorUpdating) {
      this.$bvToast.toast( this.$t('device.failedDeviceUpdate'), {
            title: this.$t('device.deviceChangeTitle'),
            autoHideDelay: 10000,
            toaster: "b-toaster-top-center",
            variant: "warning",
            append: false,
            solid: false
          })
    }
  },
  methods: {
    getPortalUrlWithoutQueryParams(){
      let urlSplit = store.getters.getUnitTenantPortalUrl.split('?')
      return urlSplit[0]
    },
    openChangeDeviceItemPage(e){
      router.push({  name: 'ChangeDeviceDetails', params:{changeRequested:e, device:this.deviceItem} })
    },
    startEditingAlias() {
      this.isEditingAlias = true
      this.originalAlias = this.deviceItem.alias || ''
      this.editedAlias = this.deviceItem.alias || ''
    },
    cancelEditingAlias() {
      this.isEditingAlias = false
      this.editedAlias = ''
      this.originalAlias = ''
    },
    onAliasChanged(changeType, value) {
      if (changeType === 'alias') {
        this.editedAlias = value
      }
    },
    saveAliasEditing(changeType, value) {
      if (changeType === 'alias') {
        this.editedAlias = value
        this.saveDevice()
      }
    },
    async saveDevice(){
      if (!this.hasChanges) {
        return
      }

      try {
        // Update alias field
        const deviceUpdate = {
          alias: this.editedAlias.trim()
        }

        if(this.deviceItem.type === 'LINKED') {
          await identityDevicesApi.updateDevice(store.getters.getSelectedUserId, this.deviceItem.guid, deviceUpdate)
        } else {
          await devicesApi.updateDevice(this.deviceItem.guid, deviceUpdate)
        }

        // Refresh device data
        let updatedDevice = undefined
        if(this.deviceItem.type === 'LINKED') {
          const result = await userApiService.getAllUserDevices(store.getters.getSelectedUserId)
          updatedDevice = result.contents.find(device => device.guid === this.deviceItem.guid)
        } else {
          const result = await devicesApi.getDevices(this.deviceItem.type)
          updatedDevice = result.contents.find(device => device.guid === this.deviceItem.guid)
        }

        if(updatedDevice && updatedDevice.lastUpdatedTime != null) {
          updatedDevice.lastUpdatedTime = new Date(updatedDevice.lastUpdatedTime).toLocaleString()
        }

        this.isEditingAlias = false
        this.editedAlias = ''
        this.originalAlias = ''

        const toastId = `device-update-toast-${Date.now()}`
        let hasNavigated = false
        
        const navigateHome = () => {
          if (!hasNavigated) {
            hasNavigated = true
            router.push({ name: 'PortalHomePage'})
          }
        }

        this.$bvToast.toast(this.$t('device.successfulDeviceUpdate'), {
          id: toastId,
          title: this.$t('device.deviceChangeTitle'),
          autoHideDelay: 10000,
          toaster: "b-toaster-top-center",
          variant: "success",
          append: false,
          solid: false
        })

        // Set up click listener on the close button (X) after toast is rendered
        this.$nextTick(() => {
          // Try multiple selectors to find the close button
          const findCloseButton = () => {
            const toastElement = document.querySelector(`#${toastId}`) || 
                                document.querySelector(`[id^="device-update-toast"]`)
            if (toastElement) {
              const closeButton = toastElement.querySelector('button[aria-label="Close"]') || 
                                 toastElement.querySelector('button.close') ||
                                 toastElement.querySelector('.toast-header button') ||
                                 toastElement.querySelector('button[type="button"]')
              if (closeButton) {
                closeButton.addEventListener('click', (e) => {
                  e.stopPropagation()
                  navigateHome()
                }, { once: true })
                return true
              }
            }
            return false
          }
          
          // Try immediately and also after a short delay in case toast renders asynchronously
          if (!findCloseButton()) {
            setTimeout(() => {
              findCloseButton()
            }, 100)
          }
        })

        // Navigate to home screen after banner auto-hides
        setTimeout(() => {
          navigateHome()
        }, 10000)
      } catch(e) {
        console.log("Error Updating Device:", e)
        this.$bvToast.toast(this.$t('device.failedDeviceUpdate'), {
          title: this.$t('device.deviceChangeTitle'),
          autoHideDelay: 10000,
          toaster: "b-toaster-top-center",
          variant: "warning",
          append: false,
          solid: false
        })
      }
    },
    async deleteDevice(){
      if(this.deviceItem.type === 'LINKED') {
        await identityDevicesApi.deleteDevice(store.getters.getSelectedUserId, this.deviceItem.guid)
      } else {
        await devicesApi.deleteDevice(this.deviceItem.guid)
      }
      router.push({ name: 'PortalHomePage'})

    }
  }
}
</script>

<style scoped>
.mt-3.d-flex .btn {
  min-height: 38px;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  border-width: 1px;
  vertical-align: middle;
}

.mt-3.d-flex .btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.mt-3.d-flex .btn:first-child,
.mt-3.d-flex .btn.me-4 {
  margin-right: 1.5rem !important;
  margin-left: 0 !important;
}

.mt-3.d-flex .btn:last-child {
  margin-left: 0 !important;
  margin-right: 0 !important;
}
</style>